import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MarkdownText } from 'components/MarkdownText'
import { SourceRow } from 'components/messages/SourceRow'
import { AgentActionCard } from 'components/messages/AgentActionCard'
import { CitationSource } from 'types/chat-message'
import { parseMessageWithSources, processCitationMarkers, parseAgentAction } from 'utils/citations'

type MessageContentProps = {
  /** Raw bot message text (may include <<<SOURCES>>>, <<<ACTION_REQUIRED>>> and citation markers). */
  text: string
}

/**
 * Shared bot message body: markdown + inline citations + sources block, plus the
 * agent action (HITL) card when the bot paused on a write action. Chrome (bubble,
 * avatar, feedback) is left to the caller.
 */
const MessageContentComponent = ({ text }: MessageContentProps) => {
  const { t } = useTranslation()

  // Split off a pending agent action first; the conversational text and any
  // continuation render around the card.
  const { text: beforeAction, action, after } = useMemo(() => parseAgentAction(text), [text])

  // Sources/citations live in the continuation (or in the whole text when no action).
  const sourceText = action ? after : text
  const { text: parsedText, sources } = useMemo(() => parseMessageWithSources(sourceText), [sourceText])
  const processedText = useMemo(() => processCitationMarkers(parsedText), [parsedText])
  const processedBefore = useMemo(() => processCitationMarkers(beforeAction), [beforeAction])

  const textSources = useMemo(() => {
    if (!sources) return [] as [string, CitationSource][]
    return Object.entries(sources).filter(([, s]) => !s.imageUrl)
  }, [sources])

  const remoteSources = useMemo(
    () => textSources.filter(([, s]) => s.type !== 'attachment' && s.type !== 'tool'),
    [textSources],
  )
  const attachmentSources = useMemo(() => textSources.filter(([, s]) => s.type === 'attachment'), [textSources])
  const toolSources = useMemo(() => textSources.filter(([, s]) => s.type === 'tool'), [textSources])

  const renderGroup = (label: string, group: [string, CitationSource][]) =>
    group.length > 0 && (
      <div className="tw-mt-3 tw-pt-2 tw-border-t" style={{ borderColor: 'var(--border-default)' }}>
        <p className="tw-text-xs tw-font-semibold tw-mb-1" style={{ color: 'var(--text-tertiary)' }}>
          {label}
        </p>
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          {group.map(([key, source]) => (
            <SourceRow key={key} sourceKey={key} source={source} />
          ))}
        </div>
      </div>
    )

  const sourcesBlock = (
    <>
      {renderGroup(remoteSources.length === 1 ? t('sourcesSingular') : t('sourcesPlural'), remoteSources)}
      {renderGroup(t('uploadedDocsTitle'), attachmentSources)}
      {renderGroup(t('toolsUsedTitle'), toolSources)}
    </>
  )

  if (action) {
    return (
      <>
        {processedBefore && <MarkdownText text={processedBefore} sources={null} />}
        <AgentActionCard action={action} resolved={!!after} />
        {after && <MarkdownText text={processedText} sources={sources} />}
        {sourcesBlock}
      </>
    )
  }

  return (
    <>
      <MarkdownText text={processedText} sources={sources} />
      {sourcesBlock}
    </>
  )
}

export const MessageContent = memo(MessageContentComponent)
